import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Navigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'

import { FacilitatorRoutes } from '../../../config/routes/routeEnums'
import { useOrganizationDetail } from '../../../apiLayer/queryHooks/queries'
import { useUpdateOrganization } from '../../../apiLayer/queryHooks/mutations'
import { Button, Input } from '../../../interaxDesignSystemComponents'
import { PageWrapper } from '../../../shared/components'
import { PageHeader } from '../../../shared/components/PageHeader'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useOrgTenant } from '../OrgContext'
import { buildOrgFormResolver, createOrgSettingsSchema, type OrgSettingsFormValues } from '../orgFormSchemas'

const cardSx = {
  padding: '24px',
  backgroundColor: INTERAX_COLORS.heritageBlue40,
  border: `1px solid ${INTERAX_COLORS.white10}`,
  borderRadius: '12px'
}

const cardTitleSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: INTERAX_COLORS.white70,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
  marginBottom: '16px'
}

const fieldLabelSx = {
  fontSize: 13,
  fontWeight: 600,
  color: INTERAX_COLORS.white70,
  mb: 1,
  letterSpacing: 0.2
}

const helperSx = {
  fontSize: 12,
  color: INTERAX_COLORS.thunderGrey,
  mt: 0.75
}

/**
 * Org admin — safe organization fields (name, address) via `updateOrganization`.
 * Two cards (Identity + Address) on the standard heritageBlue surface, with a
 * sticky-style action footer and a success indicator after save.
 */
export const OrgSettingsPage: React.FC = () => {
  const { t } = useTranslation()
  const { activeMembership, isOrgAdmin } = useOrgTenant()
  const organizationId = activeMembership?.organizationId
  const { data: orgRes } = useOrganizationDetail(organizationId)
  const org = orgRes?.data
  const updateOrg = useUpdateOrganization()

  const [copied, setCopied] = useState(false)
  const [showSavedToast, setShowSavedToast] = useState(false)

  const validationSchema = useMemo(() => createOrgSettingsSchema(t), [t])
  const resolver = useMemo(() => buildOrgFormResolver<OrgSettingsFormValues>(validationSchema), [validationSchema])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted, isValid, isDirty }
  } = useForm<OrgSettingsFormValues>({
    resolver,
    defaultValues: {
      name: '',
      street: '',
      city: '',
      country: '',
      postalCode: ''
    },
    mode: 'onSubmit'
  })

  useEffect(() => {
    if (!org) return
    reset({
      name: org.name ?? '',
      street: org.address?.street ?? '',
      city: org.address?.city ?? '',
      country: org.address?.country ?? '',
      postalCode: org.address?.postalCode ?? ''
    })
  }, [org, reset])

  // Auto-dismiss the saved toast after a short interval.
  useEffect(() => {
    if (!showSavedToast) return
    const id = setTimeout(() => setShowSavedToast(false), 2500)
    return () => clearTimeout(id)
  }, [showSavedToast])

  if (!isOrgAdmin) {
    return <Navigate to={FacilitatorRoutes.DASHBOARD} replace />
  }

  const onSubmit = handleSubmit((values) => {
    if (!organizationId || !org) return
    updateOrg.mutate(
      {
        organizationId,
        payload: {
          name: values.name.trim(),
          address: {
            street: values.street.trim(),
            city: values.city.trim(),
            country: values.country.trim(),
            postalCode: values.postalCode.trim()
          }
        }
      },
      {
        onSuccess: () => {
          setShowSavedToast(true)
          reset(values, { keepDirty: false })
        }
      }
    )
  })

  const handleCopyId = async () => {
    if (!org?.id) return
    try {
      await navigator.clipboard.writeText(org.id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API may be blocked; ignore silently.
    }
  }

  const submitDisabled = updateOrg.isPending || (isSubmitted && !isValid) || !isDirty

  return (
    <PageWrapper>
      <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ maxWidth: 720, mx: 'auto', width: '100%' }}>
        <PageHeader title={t('org.settings.title')} subtitle={t('org.settings.subtitle')} />

        {/* Identity card */}
        <Box sx={cardSx}>
          <Typography sx={cardTitleSx}>{t('org.settings.identitySection')}</Typography>

          <Box sx={{ mb: 3 }}>
            <Typography sx={fieldLabelSx}>{t('org.settings.orgId')}</Typography>
            <Input
              value={org?.id ?? ''}
              onChange={() => {
                /* read-only */
              }}
              disabled
              fullWidth
              endIcon={
                copied ? (
                  <CheckCircleIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.green }} />
                ) : (
                  <ClipboardDocumentIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.white70 }} />
                )
              }
              onEndIconClick={handleCopyId}
            />
            <Typography sx={helperSx}>{copied ? t('org.settings.idCopied') : t('org.settings.orgIdHelper')}</Typography>
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>{t('org.settings.name')}</Typography>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('org.settings.namePlaceholder')}
                  fullWidth
                  required
                  error={Boolean(errors.name)}
                  errorMessage={errors.name?.message}
                />
              )}
            />
          </Box>
        </Box>

        {/* Address card */}
        <Box sx={cardSx}>
          <Typography sx={cardTitleSx}>{t('org.settings.addressSection')}</Typography>

          <Stack spacing={2.5}>
            <Box>
              <Typography sx={fieldLabelSx}>{t('org.settings.street')}</Typography>
              <Controller
                name="street"
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('org.settings.streetPlaceholder')}
                    fullWidth
                    required
                    error={Boolean(errors.street)}
                    errorMessage={errors.street?.message}
                  />
                )}
              />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row', md: 'row' }} spacing={{ xs: 2.5, sm: 2, md: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={fieldLabelSx}>{t('org.settings.postalCode')}</Typography>
                <Controller
                  name="postalCode"
                  control={control}
                  render={({ field }) => (
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('org.settings.postalCodePlaceholder')}
                      fullWidth
                      required
                      error={Boolean(errors.postalCode)}
                      errorMessage={errors.postalCode?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <Typography sx={fieldLabelSx}>{t('org.settings.city')}</Typography>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('org.settings.cityPlaceholder')}
                      fullWidth
                      required
                      error={Boolean(errors.city)}
                      errorMessage={errors.city?.message}
                    />
                  )}
                />
              </Box>
            </Stack>

            <Box>
              <Typography sx={fieldLabelSx}>{t('org.settings.country')}</Typography>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('org.settings.countryPlaceholder')}
                    fullWidth
                    required
                    error={Boolean(errors.country)}
                    errorMessage={errors.country?.message}
                  />
                )}
              />
            </Box>
          </Stack>
        </Box>

        {updateOrg.isError && (
          <Typography role="alert" sx={{ color: INTERAX_COLORS.redError, fontSize: 14 }}>
            {updateOrg.error instanceof Error ? updateOrg.error.message : t('common.error')}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'flex-end',
            gap: 2
          }}
        >
          {showSavedToast && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.green }} />
              <Typography sx={{ fontSize: 14, color: INTERAX_COLORS.green }}>
                {t('org.settings.saveSuccess')}
              </Typography>
            </Stack>
          )}
          <Button variant="primary" onClick={onSubmit} disabled={submitDisabled}>
            {t('common.save')}
          </Button>
        </Box>
      </Stack>
    </PageWrapper>
  )
}
